import { create } from 'zustand';

/**
 * "Atuar como" (act-as / impersonate). O dev/admin loga com a **própria** conta,
 * abre uma sessão de suporte sobre o usuário-alvo (`POST
 * /admin/identity/users/:userId/impersonations`, com motivo) e, enquanto ela
 * vale, toda request leva o `x-act-as-session-id` (injetado no api-client). O
 * backend resolve a identidade **efetiva** como sendo a do alvo
 * (RoleGuard/@CurrentUser passam a operar como ele). Quem pode abrir a sessão, e
 * por quanto tempo, é decidido no backend.
 *
 * Diferente do "visualizar como" (que é só gating de UI client-side), isto muda a
 * identidade **no backend**. Por isso persiste em `sessionStorage`: sobrevive a um
 * reload (o dev está de fato trabalhando como o alvo) e some ao fechar a aba. O
 * banner vermelho mantém o estado sempre explícito + saída em um clique.
 *
 * O e-mail do alvo não entra aqui: o storage do navegador não é lugar de dado
 * da pessoa, e o banner se identifica pelo nome.
 */
const STORAGE_KEY = '@primum:impersonation';

export interface ImpersonationTarget {
  userId: string;
  name: string;
}

/** O que o `POST .../impersonations` devolve, mais quem é o alvo. */
export interface ImpersonationSession {
  sessionId: string;
  /** ISO. Passado dele o backend recusa a sessão, e a máscara cai. */
  expiresAt: string;
  target: ImpersonationTarget;
}

/**
 * Sessão guardada que não tem `sessionId` (a máscara da fase 1, só com o alvo)
 * ou que já venceu é descartada: o backend a recusaria na primeira request.
 */
function load(): ImpersonationSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const session = raw ? (JSON.parse(raw) as ImpersonationSession) : null;
    if (!session?.sessionId || !(new Date(session.expiresAt) > new Date())) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

/**
 * Roles do alvo, que o menu usa no lugar das do ator. Não persistem: depois de
 * um reload, voltam a ser buscadas (`load-target-roles.ts`).
 */
export type TargetRoles =
  | { status: 'pending' }
  | { status: 'error' }
  | { status: 'success'; roles: string[] };

interface ImpersonationState {
  target: ImpersonationTarget | null;
  sessionId: string | null;
  expiresAt: string | null;
  targetRoles: TargetRoles;
  startImpersonation: (session: ImpersonationSession) => void;
  stopImpersonation: () => void;
}

const stored = load();

export const useImpersonationStore = create<ImpersonationState>((set) => ({
  target: stored?.target ?? null,
  sessionId: stored?.sessionId ?? null,
  expiresAt: stored?.expiresAt ?? null,
  targetRoles: { status: 'pending' },
  startImpersonation: ({ sessionId, expiresAt, target }) => {
    // Montado campo a campo: um alvo com mais dados (o `UserDetail` inteiro,
    // com e-mail) não vaza para o storage.
    const session = {
      sessionId,
      expiresAt,
      target: { userId: target.userId, name: target.name },
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    set({ ...session, targetRoles: { status: 'pending' } });
  },
  stopImpersonation: () => {
    sessionStorage.removeItem(STORAGE_KEY);
    set({
      target: null,
      sessionId: null,
      expiresAt: null,
      targetRoles: { status: 'pending' },
    });
  },
}));
